import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, authHeaders, rm, PROJECTS, PROJECT_COLOR, PLATFORMS } from './marketingShared';
import './Marketing.css';

// Brand-ish dots for the platform column headers.
const PLATFORM_DOT = {
  FB: '#3b5998', Instagram: '#c13584', 'Website/SEO': '#3B5BDB',
  Google: '#1a73e8', TikTok: '#111', Others: 'var(--rmc-ink3)',
};

const pad = (n) => String(n).padStart(2, '0');
const WD = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const fmtInt = (n) => (Number(n) || 0).toLocaleString('en-MY', { maximumFractionDigits: 0 });
const fmt2 = (n) => (Number(n) || 0).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// numbers only: digits, one dot, commas
const sanitize = (s) => {
  s = (s || '').replace(/[^0-9.,]/g, '');
  const i = s.indexOf('.');
  if (i !== -1) s = s.slice(0, i + 1) + s.slice(i + 1).replace(/\./g, '');
  return s;
};
const toNum = (s) => {
  const c = sanitize(String(s)).replace(/,/g, '');
  if (c.trim() === '') return null;
  const n = parseFloat(c);
  return Number.isFinite(n) ? n : null;
};
const cellKey = (iso, platform) => `${iso}|${platform}`;

const fmtWhen = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return String(ts);
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
  return `${d.getDate()} ${mon}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

function MarketingSpend({ month }) {
  const [project, setProject] = useState('RMC');
  const [cells, setCells] = useState({});   // key -> { id, amount }
  const [audit, setAudit] = useState({});   // key -> [ {old_amount,new_amount,action,changed_by,changed_at} ]
  const [target, setTarget] = useState(0);   // spend target — synced from approved KPI Insert (read-only here)
  const [editKey, setEditKey] = useState(null);
  const [draft, setDraft] = useState('');
  const [hist, setHist] = useState(null);   // { key, top, left }
  const [error, setError] = useState('');

  // stable ref so the editing input focuses/selects once on mount (not every keystroke)
  const editRef = useCallback((el) => { if (el) { el.focus(); el.select(); } }, []);
  // suppress the input's onBlur commit when we're moving via keyboard (or cancelling)
  const navRef = useRef(false);

  // ---- month calendar (days, weekends, today, future) ----
  const [y, m] = (month || '').split('-').map(Number);
  const now = new Date();
  const curMonth = `${now.getFullYear()}-${pad(now.getMonth() + 1)}`;
  const todayISO = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

  const days = useMemo(() => {
    if (!y || !m) return [];
    const count = new Date(y, m, 0).getDate();
    const out = [];
    for (let d = 1; d <= count; d++) {
      const iso = `${y}-${pad(m)}-${pad(d)}`;
      const wd = new Date(y, m - 1, d).getDay();
      out.push({ d, iso, wd, weekend: wd === 0 || wd === 6, isToday: iso === todayISO, future: iso > todayISO });
    }
    return out;
  }, [y, m, todayISO]);

  // days elapsed for the pace projection
  const daysInMonth = days.length;
  const elapsed = month < curMonth ? daysInMonth : month === curMonth ? now.getDate() : 0;

  // ---- data loading ----
  const load = useCallback(async () => {
    setError('');
    try {
      const [sp, au] = await Promise.all([
        api.get(`/spend?month=${month}&project=${encodeURIComponent(project)}`, { headers: authHeaders() }),
        api.get(`/spend/audit?month=${month}&project=${encodeURIComponent(project)}`, { headers: authHeaders() }),
      ]);
      const c = {};
      sp.data.rows.forEach((r) => { c[cellKey(r.spend_date, r.platform)] = { id: r.id, amount: Number(r.amount) }; });
      setCells(c);
      const a = {};
      au.data.rows.forEach((r) => { (a[cellKey(r.spend_date, r.platform)] ||= []).push(r); });
      setAudit(a);
    } catch { setError('Failed to load spend'); }
    // KPI target drives only the pace meter and is staff-only (allowAdminView);
    // keep it non-fatal so the marketing role's Key in Spend page still loads.
    try {
      const kp = await api.get(`/kpi?month=${month}`, { headers: authHeaders() });
      // target = approved KPI Insert spend for this project (source of truth)
      const k = kp.data.rows.find((r) => r.kpi === 'spend' && r.project === project);
      setTarget(k ? Number(k.value) : 0);
    } catch { setTarget(0); }
  }, [month, project]);

  useEffect(() => { load(); setEditKey(null); setHist(null); }, [load]);

  const refreshAudit = useCallback(async () => {
    try {
      const au = await api.get(`/spend/audit?month=${month}&project=${encodeURIComponent(project)}`, { headers: authHeaders() });
      const a = {};
      au.data.rows.forEach((r) => { (a[cellKey(r.spend_date, r.platform)] ||= []).push(r); });
      setAudit(a);
    } catch { /* non-fatal */ }
  }, [month, project]);

  // ---- save a single cell ----
  const commit = useCallback(async (key, valueStr) => {
    const [iso, platform] = key.split('|');
    const num = toNum(valueStr);
    const existing = cells[key];
    try {
      if (num === null) {
        if (existing && existing.id) {
          await api.delete(`/spend/${existing.id}`, { headers: authHeaders() });
          setCells((p) => { const n = { ...p }; delete n[key]; return n; });
          refreshAudit();
        }
        return;
      }
      if (existing && existing.amount === num) return;   // no change
      const res = await api.post('/spend', { spend_date: iso, project, platform, amount: num }, { headers: authHeaders() });
      setCells((p) => ({ ...p, [key]: { id: res.data.id, amount: Number(res.data.amount) } }));
      refreshAudit();
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save spend');
    }
  }, [cells, project, refreshAudit]);

  // ---- edit navigation ----
  const beginEdit = (key) => {
    const [iso] = key.split('|');
    if (iso > todayISO) return;                 // can't log future spend
    setHist(null);
    setEditKey(key);
    setDraft(cells[key] ? String(cells[key].amount) : '');
  };
  const moveDay = (key, dir) => {
    const [iso, platform] = key.split('|');
    const idx = days.findIndex((x) => x.iso === iso);
    const next = days[idx + dir];
    if (next && !next.future) beginEdit(cellKey(next.iso, platform));
    else setEditKey(null);
  };
  const movePlatform = (key, back) => {
    const [iso, platform] = key.split('|');
    const pi = PLATFORMS.indexOf(platform);
    const np = PLATFORMS[pi + (back ? -1 : 1)];
    if (np) beginEdit(cellKey(iso, np));
    else moveDay(key, back ? -1 : 1);
  };
  const onCellKeyDown = (e, key) => {
    if (e.key === 'Enter') { e.preventDefault(); navRef.current = true; commit(key, draft); moveDay(key, 1); }
    else if (e.key === 'Escape') { e.preventDefault(); navRef.current = true; setEditKey(null); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); navRef.current = true; commit(key, draft); moveDay(key, 1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); navRef.current = true; commit(key, draft); moveDay(key, -1); }
    else if (e.key === 'Tab') { e.preventDefault(); navRef.current = true; commit(key, draft); movePlatform(key, e.shiftKey); }
  };


  // ---- history popover ----
  const openHist = (e, key) => {
    e.stopPropagation();
    const r = e.currentTarget.getBoundingClientRect();
    setHist({ key, top: window.scrollY + r.bottom + 6, left: window.scrollX + r.left });
  };
  useEffect(() => {
    if (!hist) return;
    const close = (e) => { if (!e.target.closest('.mk-hist-pop') && !e.target.closest('.mk-hist-btn')) setHist(null); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [hist]);

  // ---- totals + budget meter ----
  const platTotals = PLATFORMS.map((p) =>
    days.reduce((a, day) => a + (cells[cellKey(day.iso, p)]?.amount || 0), 0));
  const total = platTotals.reduce((a, b) => a + b, 0);
  const pct = target > 0 ? (total / target) * 100 : 0;
  const proj = elapsed > 0 ? (total / elapsed) * daysInMonth : 0;
  const projPct = target > 0 ? (proj / target) * 100 : 0;
  const barColor = pct > 100 ? 'var(--rmc-coral)' : pct >= 80 ? 'var(--rmc-amber)' : 'var(--rmc-green)';
  const paceColor = projPct > 100 ? 'var(--rmc-coral)' : projPct >= 90 ? 'var(--rmc-amber)' : 'var(--rmc-green)';
  const paceLabel = projPct > 100 ? 'Over pace · trim spend' : projPct >= 90 ? 'Watch pace' : 'On track';

  const pc = PROJECT_COLOR[project];
  const entryCount = Object.keys(cells).length;

  const renderCell = (day, platform) => {
    const key = cellKey(day.iso, platform);
    const cell = cells[key];
    const h = audit[key] || [];
    const edited = h.length > 1;
    const editing = editKey === key;
    const cls = ['mk-cell'];
    if (cell) cls.push('filled'); else cls.push('empty');
    if (day.future) cls.push('future');
    if (editing) cls.push('editing');
    if (edited) cls.push('edited');
    return (
      <td key={platform} className={cls.join(' ')} onClick={() => !editing && !day.future && beginEdit(key)}>
        {editing ? (
          <input
            className="mk-cell-input"
            inputMode="decimal"
            autoComplete="off"
            value={draft}
            ref={editRef}
            onChange={(e) => setDraft(sanitize(e.target.value))}
            onKeyDown={(e) => onCellKeyDown(e, key)}
            onBlur={() => { if (navRef.current) { navRef.current = false; return; } commit(key, draft); setEditKey(null); }}
          />
        ) : (
          <span className="mk-cell-val">{cell ? fmt2(cell.amount) : ''}</span>
        )}
        {!editing && !day.future && h.length > 0 && (
          <button className="mk-hist-btn" tabIndex={-1} title={edited ? `${h.length} edits — click for history` : 'Edit history'}
                  onClick={(e) => openHist(e, key)}>
            {edited ? h.length : '\u{1F552}'}
          </button>
        )}
      </td>
    );
  };

  return (
    <div className="mk-page">
      <div className="mk-head">
        <div>
          <div className="mk-crumbs">Marketing Report &rsaquo; Spend Report &rsaquo; <b>Key in Spend</b> &rsaquo; {project}</div>
          <h1>Key in Spend</h1>
          <div className="sub">{month} · one cell per platform × day · click a cell to edit, Enter to save</div>
        </div>
      </div>

      <div className="mk-banner ok">
        <span>✓</span>
        <span><b>Click a cell to edit · Enter to save</b> (Esc cancels; Enter jumps to the next day). Same date + platform overwrites — no duplicates. Every filled cell keeps an <b>edit history</b> (🕒). Numbers only.</span>
      </div>

      {error && <div className="mk-error">{error}</div>}

      {/* Project tabs */}
      <div className="proj-tabs">
        {PROJECTS.map((p) => (
          <div key={p} className={`proj-tab ${p === project ? 'active' : ''}`} style={{ '--pc': PROJECT_COLOR[p] }}
               onClick={() => setProject(p)}>
            <span className="dot" style={{ background: PROJECT_COLOR[p] }} />{p}
          </div>
        ))}
      </div>

      <div className="mk-card">
        <div className="mk-card-head">
          <h3>{project} Spend — {month} <span className="proj-badge"><span className="dot" style={{ background: pc }} />{project}</span></h3>
          <span className="c-sub">{entryCount} entries logged</span>
        </div>

        {/* Spend vs KPI target — target synced from approved KPI Insert (read-only here) */}
        <div className="mk-budget" style={{ '--bc': barColor }}>
          <div className="mk-budget-top">
            <div>
              <span className="b-spent">{rm(total)}</span>
              <span className="b-of">logged of {target > 0
                ? <>{rm(target)} <span className="b-kpi-tag">KPI target</span></>
                : <>no KPI spend set for {project} — <b>enter it on KPI Insert</b></>}</span>
            </div>
            {target > 0 && (
              <div className="b-pctwrap"><span className="b-pct">{Math.round(pct)}<small>%</small></span><div className="b-pctlbl">of KPI target</div></div>
            )}
          </div>
          {target > 0 && (
            <>
              <div className="b-meter">
                <div className="b-track"><div className={`b-fill${pct > 100 ? ' over' : ''}`} style={{ width: `${Math.min(100, pct)}%` }} /></div>
                {elapsed > 0 && (
                  <div className="b-proj" style={{ left: `${Math.max(3, Math.min(97, projPct))}%` }}
                       title={`At the current daily rate you’ll log about ${rm(proj)} (${Math.round(projPct)}% of the KPI target) by month end.`}>
                    <span className="b-proj-l">by {pad(daysInMonth)} → {Math.round(projPct)}%</span>
                  </div>
                )}
              </div>
              <div className="mk-budget-bot">
                {elapsed > 0
                  ? <span className="b-chip" style={{ '--bc': paceColor }}><span className="dot" />{paceLabel} · projected {rm(proj)}</span>
                  : <span className="b-chip" style={{ '--bc': 'var(--rmc-ink3)' }}><span className="dot" />future month</span>}
                <span className="b-remain">{total <= target ? <><b>{rm(target - total)}</b> to target</> : <><b>{rm(total - target)}</b> over target</>}</span>
              </div>
            </>
          )}
        </div>

        {/* Grid */}
        <div className="mk-gridscroll">
          <table className="mk-grid">
            <thead>
              <tr>
                <th className="corner">Day</th>
                {PLATFORMS.map((p) => (
                  <th key={p}><span className="dot" style={{ background: PLATFORM_DOT[p] }} />{p}</th>
                ))}
                <th className="tot">Day total</th>
              </tr>
            </thead>
            <tbody>
              {days.map((day) => {
                const rowTotal = PLATFORMS.reduce((a, p) => a + (cells[cellKey(day.iso, p)]?.amount || 0), 0);
                const hasAny = PLATFORMS.some((p) => cells[cellKey(day.iso, p)]);
                const cls = [];
                if (day.weekend) cls.push('wknd');
                if (day.isToday) cls.push('today');
                if (day.future) cls.push('future');
                return (
                  <tr key={day.iso} className={cls.join(' ')}>
                    <td className="day"><span className="wd">{WD[day.wd]}</span>{day.d}{day.isToday && <span className="todaytag">TODAY</span>}</td>
                    {PLATFORMS.map((p) => renderCell(day, p))}
                    <td className="rowtot">{hasAny ? fmtInt(rowTotal) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="coltot">
                <td className="day">Total</td>
                {platTotals.map((t, i) => <td key={i}>{t ? fmtInt(t) : '—'}</td>)}
                <td className="grand">{fmtInt(total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p className="mk-foot">The <b>Marketing Report</b> rolls these into spend-by-project, spend-by-platform and cost-per-enquiry automatically.</p>
      </div>

      {/* History popover */}
      {hist && audit[hist.key] && (
        <div className="mk-hist-pop" style={{ top: hist.top, left: hist.left }}>
          <h4>
            <span className="dot" style={{ background: PLATFORM_DOT[hist.key.split('|')[1]] }} />
            {hist.key.split('|')[1]} · {hist.key.split('|')[0].slice(8)}/{hist.key.split('|')[0].slice(5, 7)}
          </h4>
          {audit[hist.key].slice().reverse().map((e, i) => {
            const cur = i === 0;
            const val = e.action === 'delete' ? 'cleared' : `RM ${fmt2(e.new_amount)}`;
            return (
              <div key={i} className={`he ${cur ? 'cur' : 'old'}`}>
                <span className="v">{val}{cur && <em>current</em>}</span>
                <span className="m">{e.changed_by}<br />{fmtWhen(e.changed_at)}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MarketingSpend;
